import axios from "axios";
import { AppThunk } from "store/store";
import { API_ENDPOINTS } from "config/apiConfig";
import { axiosInstance } from "config/axiosConfig";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { hideLoading, showLoading } from "./loadingSlice";
import {
  ProductInfo,
  ProductFilter,
  Product,
  ProductList,
  ProductListPaginate,
} from "common/interfaces";
import { PAGINATION } from "common/enums/Pagination.enum";
import { normalizeParams } from "common/utils";

interface UserRegisterState {
  productData: ProductListPaginate | null;
  productLimit: ProductList | null;
  productDetailData: {
    data: Product;
    message: string;
    success: boolean;
  } | null;
  createProductData: {
    data: Product;
    message: string;
    success: boolean;
  } | null;
  editProductData: {
    data: Product;
    message: string;
    success: boolean;
  } | null;
  error: string | null;
}

const initialState: UserRegisterState = {
  productData: null,
  productLimit: null,
  productDetailData: null,
  createProductData: null,
  editProductData: null,
  error: null,
};

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    productSuccess: (state, action: PayloadAction<any>) => {
      state.productData = action.payload;
      state.error = null;
    },
    productFail: (state, action: PayloadAction<string>) => {
      state.productData = null;
      state.error = action.payload;
    },
    productLimitSuccess: (state, action: PayloadAction<any>) => {
      state.productLimit = action.payload;
      state.error = null;
    },
    productLimitFail: (state, action: PayloadAction<string>) => {
      state.productLimit = null;
      state.error = action.payload;
    },
    productDetailSuccess: (state, action: PayloadAction<any>) => {
      state.productDetailData = action.payload;
      state.error = null;
    },
    productDetailFail: (state, action: PayloadAction<string>) => {
      state.productDetailData = null;
      state.error = action.payload;
    },
    createProductSuccess: (state, action: PayloadAction<any>) => {
      state.createProductData = action.payload;
      state.error = null;
    },
    createProductFail: (state, action: PayloadAction<string>) => {
      state.createProductData = null;
      state.error = action.payload;
    },
    editProductSuccess: (state, action: PayloadAction<any>) => {
      state.editProductData = action.payload;
      state.error = null;
    },
    editProductFail: (state, action: PayloadAction<string>) => {
      state.editProductData = null;
      state.error = action.payload;
    },
    productCleanData: (state) => {
      state.productData = null;
      state.productLimit = null;
      state.createProductData = null;
      state.error = null;
    },
  },
});

export const {
  productSuccess,
  productFail,
  productDetailSuccess,
  productDetailFail,
  createProductSuccess,
  createProductFail,
  editProductSuccess,
  editProductFail,
  productCleanData,
  productLimitSuccess,
  productLimitFail,
} = productSlice.actions;

export const fetchGetProduct =
  (params: object, signal: AbortSignal): AppThunk =>
  async (dispatch) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.PRODUCT_LIST, {
        params: params,
        signal,
      });
      dispatch(productLimitSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(productLimitFail(error.message || "Something went wrong"));
      }
    }
  };

export const fetchProducts =
  (
    productFilter: ProductFilter,
    currentPage: number | undefined,
    signal: AbortSignal,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(showLoading());
    try {
      const normalizedFilter = normalizeParams({
        ...productFilter,
        sort: productFilter.sort ? productFilter.sort : undefined,
      });
      const response = await axiosInstance.get(API_ENDPOINTS.PRODUCT_LIST, {
        params: {
          page: currentPage || PAGINATION.PAGE,
          limit: PAGINATION.LIMIT,
          ...normalizedFilter,
        },
        signal,
        paramsSerializer: {
          indexes: null,
        },
      });
      dispatch(productSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(productFail(error.message || "Something went wrong"));
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const fetchProductDetail =
  (productCode: string, signal: AbortSignal): AppThunk =>
  async (dispatch) => {
    dispatch(showLoading());
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.PRODUCT_DETAIL}/${productCode}`,
        {
          signal,
        },
      );
      dispatch(productDetailSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(productDetailFail(error.message || "Something went wrong"));
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const createProduct =
  (productInfo: ProductInfo, signal: AbortSignal): AppThunk =>
  async (dispatch) => {
    dispatch(showLoading());
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.CREATE_PRODUCT,
        productInfo,
        { signal },
      );
      dispatch(createProductSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(createProductFail(error.message || "Something went wrong"));
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const editProduct =
  (
    productCode: string,
    productInfo: ProductInfo,
    signal: AbortSignal,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(showLoading());
    try {
      const response = await axiosInstance.put(
        `${API_ENDPOINTS.EDIT_PRODUCT}/${productCode}`,
        productInfo,
        { signal },
      );
      dispatch(editProductSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(editProductFail(error.message || "Something went wrong"));
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export default productSlice.reducer;
